import { useMemo } from 'react';
import { parseURL } from 'ufo';
import { calculateImageDimension } from '@/components/common/media-content/image-dimension';
import { useSafeMediaUrl } from '@/hooks/use-safe-media-url';
import { useIsDesktop } from '@/hooks/use-user-platform';

interface PreloadImageOptions {
  maxWidth?: number;
  size?: [number, number];
}

export default function usePreloadImage(src: Nullable<string>, options: PreloadImageOptions = {}) {
  const { size, maxWidth } = options;
  const { src: safeSrc, fileEndpoint } = useSafeMediaUrl(src);
  const isDesktop = useIsDesktop();

  const [respectiveWidth, respectiveHeight, aspectRatio] = useMemo<[number, number, number]>(() => {
    const url = parseURL(safeSrc);

    if (size) return [...size, size[0] / size[1]] as const;

    const searchParams = new URLSearchParams(url.search);
    const widthParam = searchParams.get('w');
    const heightParam = searchParams.get('h');

    let [width, height] = [Number(widthParam), Number(heightParam)] as const;
    if (Number.isNaN(width) || Number.isNaN(height)) {
      width = 150;
      height = 150;
    }

    return calculateImageDimension(width, height, {
      maxWidth: maxWidth,
      maxHeight: isDesktop ? 0.4 : 0.25,
    });
  }, [isDesktop, maxWidth, size, safeSrc]);

  return {
    src: safeSrc,
    fileEndpoint,
    aspectRatio,
    dimension: [respectiveWidth, respectiveHeight] as const,
  };
}
